import styles from "./Meter.module.css";

// A ratio bar: "38 of 42 teams". Not in @marekl/adminer — every admin panel
// wants one, so it is listed as a promotion candidate in
// docs/adminer-redesign.md rather than intended to live here forever.
//
// It renders a real `progressbar` role: a bar that only exists visually is
// invisible to a screen reader, and a ratio is exactly the thing someone
// using one needs told.

export type MeterVariant = "primary" | "ok" | "warn";

export interface MeterProps {
  value: number;
  max: number;
  variant?: MeterVariant;
  /** Describes what the ratio is of; required, since the bar has no text. */
  label: string;
  className?: string;
}

export function Meter({ value, max, variant = "primary", label, className }: MeterProps) {
  // A max of 0 is a legitimate state (no teams yet), not an error — render an
  // empty track rather than dividing by zero.
  const safeMax = max > 0 ? max : 0;
  const clamped = safeMax === 0 ? 0 : Math.min(Math.max(value, 0), safeMax);
  const pct = safeMax === 0 ? 0 : (clamped / safeMax) * 100;

  return (
    <div
      className={[styles.track, className].filter(Boolean).join(" ")}
      role="progressbar"
      aria-label={label}
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={safeMax}
    >
      <div
        className={[styles.bar, variant !== "primary" ? styles[variant] : ""]
          .filter(Boolean)
          .join(" ")}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
